import React, { useState, useRef, useMemo, useCallback } from "react";
import Modal from "@mui/material/Modal";
import { useSelector } from "react-redux";
import { Fade } from "@mui/material";
import CustomIconButton from "../../../components/CustomIconButton";
import CustomButton from "../../../components/CustomButton";
import QRCodeWithLogo from "../../../components/QRCodeWithLogo";
import { TYPE_MAP } from "../../../constants/index";
import { downloadQRCodeWithLogo, handleCopy } from "../../../utils/util";
import useTrackPageView from "../../../hooks/useTrackPageView";

const FriendReferralPopup = ({ open, onClose, images }: any) => {
    const [selectedTab, setSelectedTab] = useState("alumni");
    const { friendImg1, friendImg2 } = images;
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const { talentInfo } = useSelector((state: any) => state.currentUser);
    const registerLinks = talentInfo?.company?.register_links;
    const [isShowMsg, setIsShowMsg] = useState(false);
    const alumniLink = registerLinks && registerLinks.find(link => link.type === "alumni");
    const otherLink = registerLinks && registerLinks.find(link => link.type === "external_talent");

    useTrackPageView({ urlPath: "/talent/invitation" })

    const triggerMessage = useCallback(() => {
        setIsShowMsg(true);
        setTimeout(() => setIsShowMsg(false), 3000);
    }, []);

    const referralURL = useMemo(() => {
        if (!talentInfo || !registerLinks) return "";

        const target = selectedTab === "alumni" ? alumniLink : otherLink;
        return `${window.location.origin}/new/${TYPE_MAP[target?.type]}/${talentInfo.company?.url_slug}?c=${target?.code}&t=${talentInfo?.id_base62}`;
    }, [selectedTab, talentInfo, registerLinks]);

    const onCopy = useCallback(() => {
        handleCopy(referralURL, triggerMessage);
    }, [referralURL, triggerMessage]);

    const onDownload = useCallback(() => {
        const target = selectedTab === "alumni" ? registerLinks?.[0] : registerLinks?.[1];
        downloadQRCodeWithLogo(canvasRef.current, `QR${target?.code}.png`);
    }, [selectedTab, registerLinks]);

    return (
        <Modal open={open} onClose={onClose} disableEnforceFocus>
            <Fade in={open} timeout={0}>
                <div
                    className="qr-bg-class outline-none rounded-2xl min-w-[22.5rem] max-w-[22.5rem] 
                               absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                >
                    <div className="flex items-center justify-between mb-8 px-4 mt-4">
                        <h3 className="alu-text-base-2 text-xl font-bold">友達を招待</h3>
                        <CustomIconButton onClick={onClose}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
                                <path
                                    d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </CustomIconButton>
                    </div>

                    <div className="max-h-[70vh] overflow-y-auto custom-scrollbar scrollbar-hide px-4 pb-[2.3125rem]">

                        {/* Tabs */}
                        <div className="relative h-9 rounded-[1.125rem] border-2 border-[#FF861A] bg-white p-1 
                                        flex items-center gap-1 mb-2">
                            <div
                                className={`absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-2xl bg-[#FF861A] 
                                            transition-all duration-300 
                                            ${selectedTab === "alumni" ? "left-1" : "left-[calc(50%)]"}`}
                            ></div>

                            <div
                                className={`w-1/2 px-3 py-1 text-center text-sm rounded-2xl relative z-10 
                                            transition-colors duration-300 
                                            ${selectedTab === "alumni" ? "text-white font-bold" : "text-[#808080] cursor-pointer hover:bg-[#FFF3E5] hover:text-[#343A3D]"}`}
                                onClick={() => setSelectedTab("alumni")}
                            >
                                卒業生を紹介
                            </div>

                            <div
                                className={`w-1/2 py-1 text-center text-sm rounded-2xl relative z-10 
                                            transition-colors duration-300 
                                            ${selectedTab === "external_talent" ? "text-white font-bold" : "text-[#808080] cursor-pointer hover:bg-[#FFF3E5] hover:text-[#343A3D]"}`}
                                onClick={() => setSelectedTab("external_talent")}
                            >
                                その他の友だちを招待
                            </div>
                        </div>

                        {/* QR Section */}
                        <div className="relative w-full h-[15.125rem] px-3 py-4 flex items-center justify-center 
                                        rounded-lg bg-white border border-[#E0E0E0] mb-4">
                            <QRCodeWithLogo value={referralURL} canvasRef={canvasRef} size={160} marginSize={1} />
                            <img src={friendImg1} alt="" className="object-contain absolute bottom-6 right-[0.875rem]" />

                            <p className={`absolute left-1/2 -translate-x-1/2 bottom-2 text-xs 
                                          transition-opacity duration-300 text-[#000] leading-[18px]  
                                          ${isShowMsg ? "opacity-100" : "opacity-0"}`}>
                                コピーしました！
                            </p>
                        </div>

                        <div className="w-full flex flex-col gap-2 mb-6">
                            <CustomButton disabled={isShowMsg} width="full" variant="gray" size="md" onClick={onCopy}>
                                リンクをコピー
                            </CustomButton>

                            <CustomButton width="full" variant="gray" size="md" onClick={onDownload}>
                                QRコードを保存
                            </CustomButton>
                        </div>

                        <div className="w-full flex gap-2">
                            <img src={friendImg2} alt="" className="object-contain self-start" />
                            <div className="rounded-2xl p-4 bg-white alu-text-base-2 text-xs leading-[1.125rem] alu-friend-invitation-box-shadow tracking-[0.28px]">
                                <p className="font-bold">
                                    aloop でしか見れない企業のニュースや、最新の求人情報にアクセスできる機会をお友達に提供してみませんか？
                                    <br />
                                    キャリアが広がり、次のチャンスを見つけるきっかけにもなります！
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Fade>
        </Modal>
    );
};

export default FriendReferralPopup;
